/**
 * ADA patch: keyboard accessibility for the Calendly "Book a Consultation"
 * popup (opened from the header / footer links via Calendly.initPopupWidget).
 *
 * Calendly injects <div class="calendly-overlay"> with a <div> close button
 * that cannot receive keyboard focus, never moves focus into the dialog, and
 * does not respond to Escape. This patch (no core file changes):
 *   - remembers the trigger and restores focus to it when the popup closes
 *   - marks the popup as role="dialog" / aria-modal
 *   - makes the close button a focusable button operable with Enter / Space
 *   - moves focus into the scheduler iframe when the popup opens
 *   - closes the popup on Escape and keeps Tab cycling inside the dialog
 */
(function () {
  'use strict';

  var lastTrigger = null;
  var activeOverlay = null; // set while a popup is open

  // Capture phase so this runs before Calendly's own inline onclick handler.
  document.addEventListener(
    'click',
    function (evt) {
      if (!evt.target || !evt.target.closest) return;
      var trigger = evt.target.closest('[onclick*="Calendly.initPopupWidget"]');
      if (trigger) lastTrigger = trigger;
    },
    true
  );

  // Hard focus guard: while a popup is open, if focus ever lands OUTSIDE the
  // overlay, pull it straight back to the close button. This catches any
  // escape route (background element, stray focusable) regardless of inert.
  document.addEventListener('focusin', function (evt) {
    if (!activeOverlay) return;
    if (activeOverlay.contains(evt.target)) return;
    // Pull escaped focus back to the ring-less dialog container (not the X),
    // so recovering focus never flashes a "selected" close button.
    var target =
      activeOverlay.querySelector('.calendly-popup') ||
      findClose(activeOverlay) ||
      activeOverlay.querySelector('iframe');
    if (target) {
      window.setTimeout(function () {
        target.focus();
      }, 0);
    }
  });

  function closePopup(overlay) {
    // Prefer Calendly's official API — a synthetic click on their close
    // element does not reliably trigger their close handler.
    if (window.Calendly && typeof window.Calendly.closePopupWidget === 'function') {
      window.Calendly.closePopupWidget();
      return;
    }
    var close = overlay && overlay.querySelector('.calendly-popup-close, .calendly-close-overlay');
    if (close) close.click();
    // Last resort: remove the overlay ourselves so the modal is dismissed.
    if (overlay && overlay.parentNode) {
      window.setTimeout(function () {
        if (document.body.contains(overlay)) overlay.parentNode.removeChild(overlay);
      }, 50);
    }
  }

  // Modal focus trap: make everything behind the overlay inert (not focusable,
  // hidden from assistive tech) so focus is confined to the popup. This works
  // even though the scheduler is a cross-origin iframe we cannot script.
  function inertElement(el) {
    if (el.hasAttribute('data-ada-inert')) return;
    el.setAttribute('data-ada-inert', '');
    if (el.hasAttribute('aria-hidden')) {
      el.setAttribute('data-ada-prev-ariahidden', el.getAttribute('aria-hidden'));
    }
    el.setAttribute('aria-hidden', 'true');
    try { el.inert = true; } catch (e) { /* older browsers */ }
  }

  // Hide every sibling at every level from the overlay up to <body>, so the
  // popup is the only interactive subtree — correct even if Calendly nests the
  // overlay inside a wrapper rather than placing it directly under <body>.
  function setBackgroundInert(overlay) {
    var node = overlay;
    while (node && node !== document.body && node.parentElement) {
      var parent = node.parentElement;
      var siblings = parent.children;
      for (var i = 0; i < siblings.length; i++) {
        if (siblings[i] !== node) inertElement(siblings[i]);
      }
      node = parent;
    }
  }

  function clearBackgroundInert() {
    var marked = document.querySelectorAll('[data-ada-inert]');
    for (var i = 0; i < marked.length; i++) {
      var child = marked[i];
      child.removeAttribute('data-ada-inert');
      try { child.inert = false; } catch (e) { /* ignore */ }
      if (child.hasAttribute('data-ada-prev-ariahidden')) {
        child.setAttribute('aria-hidden', child.getAttribute('data-ada-prev-ariahidden'));
        child.removeAttribute('data-ada-prev-ariahidden');
      } else {
        child.removeAttribute('aria-hidden');
      }
    }
  }

  function findClose(overlay) {
    // Target ONLY the visible X button, never the big transparent
    // .calendly-close-overlay backdrop (which would put a huge empty focus
    // box in the corner instead of hugging the X).
    return overlay.querySelector('.calendly-popup-close, [class*="popup-close"]');
  }

  function enhanceClose(close, overlay) {
    if (!close || close.dataset.adaClosePatched) return;
    close.dataset.adaClosePatched = 'true';
    close.setAttribute('role', 'button');
    close.setAttribute('tabindex', '0');
    if (!close.getAttribute('aria-label')) {
      close.setAttribute('aria-label', 'Close scheduling popup');
    }
    // Both keyboard AND mouse route through the reliable API-based close.
    function activate(evt) {
      evt.preventDefault();
      closePopup(overlay);
    }
    close.addEventListener('click', activate);
    close.addEventListener('keydown', function (evt) {
      if (evt.key === 'Enter' || evt.key === ' ' || evt.key === 'Spacebar') {
        activate(evt);
      }
    });
  }

  function patchOverlay(overlay) {
    if (overlay.dataset.adaCalendlyPatched) return;
    overlay.dataset.adaCalendlyPatched = 'true';

    var popup = overlay.querySelector('.calendly-popup') || overlay;
    popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-modal', 'true');
    popup.setAttribute('aria-label', 'Book a consultation - scheduling');
    popup.setAttribute('tabindex', '-1'); // focusable for initial focus, no ring

    var iframe = overlay.querySelector('iframe');
    if (iframe && !iframe.hasAttribute('title')) {
      iframe.setAttribute('title', 'Calendly scheduling page');
    }

    // Trap focus: everything behind the popup becomes inert (removes it from
    // tab order + hides from assistive tech), and the focusin guard above
    // pulls back any focus that still escapes.
    activeOverlay = overlay;
    setBackgroundInert(overlay);

    // Focus trap around the CROSS-ORIGIN iframe. Key handlers can't catch Tab
    // while focus is inside Calendly's iframe, so we place invisible focusable
    // "sentinels" before and after the popup content. When Tab moves focus out
    // of the iframe it lands on a sentinel (a parent-page element whose focus
    // event DOES fire), and we bounce focus back inside the dialog.
    function makeSentinel() {
      var s = document.createElement('span');
      s.tabIndex = 0;
      s.dataset.adaSentinel = '1';
      s.setAttribute('aria-hidden', 'true');
      s.style.cssText =
        'position:fixed;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;opacity:0;';
      return s;
    }
    var startSentinel = makeSentinel();
    var endSentinel = makeSentinel();
    overlay.insertBefore(startSentinel, overlay.firstChild);
    overlay.appendChild(endSentinel);

    startSentinel.addEventListener('focus', function () {
      // Shift+Tab off the first control -> wrap to the scheduler (end region).
      var frame = overlay.querySelector('iframe');
      (frame || findClose(overlay) || endSentinel).focus();
    });
    endSentinel.addEventListener('focus', function () {
      // Tab off the end (out of the iframe) -> wrap to the close button (start).
      var close = findClose(overlay);
      (close || overlay.querySelector('iframe') || startSentinel).focus();
    });

    // Initial focus goes to the dialog CONTAINER (no visible ring), not the X,
    // so nothing looks "selected" on open. The close button's ring only shows
    // when the user actually Tabs to it (:focus-visible).
    window.setTimeout(function () {
      if (document.body.contains(popup)) popup.focus();
    }, 150);

    // Calendly injects the close (X) button asynchronously, so poll for it and
    // make it keyboard-operable (but do NOT auto-focus it).
    var tries = 0;
    (function grabClose() {
      var close = findClose(overlay);
      if (close) {
        enhanceClose(close, overlay);
        return;
      }
      if (tries++ < 30) window.setTimeout(grabClose, 100); // up to ~3s
    })();

    // Keep the close button keyboard-ready even if Calendly re-renders it.
    new MutationObserver(function () {
      enhanceClose(findClose(overlay), overlay);
    }).observe(overlay, { childList: true, subtree: true });

    // Escape closes the dialog (works whenever focus is on a parent-page
    // element such as the close button or a sentinel).
    overlay.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape' || evt.key === 'Esc') {
        evt.preventDefault();
        closePopup(overlay);
      }
    });
  }

  function containsOverlay(node) {
    if (node.nodeType !== 1) return null;
    if (node.classList && node.classList.contains('calendly-overlay')) return node;
    return node.querySelector ? node.querySelector('.calendly-overlay') : null;
  }

  var observer = new MutationObserver(function (mutations) {
    for (var m = 0; m < mutations.length; m++) {
      var added = mutations[m].addedNodes;
      for (var i = 0; i < added.length; i++) {
        var overlay = containsOverlay(added[i]);
        if (overlay) patchOverlay(overlay);
      }

      var removed = mutations[m].removedNodes;
      for (var j = 0; j < removed.length; j++) {
        if (containsOverlay(removed[j])) {
          // Popup closed: stop guarding, release the background, restore focus.
          activeOverlay = null;
          clearBackgroundInert();
          if (lastTrigger && document.body.contains(lastTrigger)) {
            lastTrigger.focus();
          }
        }
      }
    }
  });

  function init() {
    observer.observe(document.body, { childList: true, subtree: true });

    // Let assistive tech know the triggers open a dialog.
    var triggers = document.querySelectorAll('[onclick*="Calendly.initPopupWidget"]');
    for (var i = 0; i < triggers.length; i++) {
      triggers[i].setAttribute('aria-haspopup', 'dialog');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
